import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import Slider from "react-slick";
import { useNavigate } from "react-router-dom";

import { axiosGet } from "../../Apis/axiosMethods";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];
const VIDEO_EXTENSIONS = ["mp4", "webm", "ogg"];

const NewArrivalsBanner = () => {
    const navigate = useNavigate();

    const [banners, setBanners] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);

    // Fetch banners
    useEffect(() => {
        let cancelled = false;

        const getBanners = async () => {
            try {
                const response = await axiosGet(
                    "/Dashboard/GetHomeDashboardBannerSliders",
                    { status: 2 }
                );

                if (cancelled) return;

                if (response?.data?.success) {
                    setBanners(
                        Array.isArray(response.data.data)
                            ? response.data.data
                            : []
                    );
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("New Arrivals Banner Error:", error);
                }
            }
        };

        getBanners();

        return () => {
            cancelled = true;
        };
    }, []);

    // Get file extension
    const getFileExtension = useCallback((url) => {
        if (!url) return "";

        return url.split("?")[0].split(".").pop().toLowerCase();
    }, []);

    // Check whether any banner is a video
    const hasVideo = useMemo(
        () =>
            banners.some((banner) =>
                VIDEO_EXTENSIONS.includes(
                    getFileExtension(banner.business_home_banner_path_url)
                )
            ),
        [banners, getFileExtension]
    );

    // Slider settings
    const settings = useMemo(
        () => ({
            dots: true,

            // Avoid cloned video slides and duplicate video requests
            infinite: banners.length > 1 && !hasVideo,

            autoplay: banners.length > 1,
            autoplaySpeed: 5000,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            speed: 500,

            afterChange: (index) => {
                setActiveIndex(
                    banners.length ? index % banners.length : 0
                );
            },
        }),
        [banners.length, hasVideo]
    );

    // Handle banner click
    const handleBannerClick = useCallback(
        (link) => {
            if (!link) return;

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });

            if (link.startsWith("/")) {
                navigate(link);
            } else {
                window.open(link, "_self");
            }
        },
        [navigate]
    );

    if (!banners.length) return null;

    const nextIndex = (activeIndex + 1) % banners.length;

    return (
        <div className="container">
            <div className="row">
                <div className="col-12" style={{ height: "250px" }}>
                    <Slider {...settings}>
                        {banners.map((banner, index) => {
                            const url = banner.business_home_banner_path_url;
                            const ext = getFileExtension(url);

                            const isActive = index === activeIndex;
                            const shouldLoadImage =
                                isActive || index === nextIndex;

                            return (
                                <div
                                    key={banner.business_home_banner_id}
                                >
                                    {VIDEO_EXTENSIONS.includes(ext) && (
                                        <video
                                            src={isActive ? url : undefined}
                                            autoPlay={isActive}
                                            muted
                                            loop
                                            playsInline
                                            preload={
                                                isActive ? "auto" : "none"
                                            }
                                            width="1200"
                                            height="250"
                                            style={{
                                                width: "100%",
                                                height: "250px",
                                                objectFit: "contain",
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                handleBannerClick(
                                                    banner.business_home_banner_link
                                                )
                                            }
                                        />
                                    )}

                                    {IMAGE_EXTENSIONS.includes(ext) && (
                                        <img
                                            src={
                                                shouldLoadImage
                                                    ? url
                                                    : undefined
                                            }
                                            alt={`New arrivals banner ${index + 1}`}
                                            loading={
                                                isActive ? "eager" : "lazy"
                                            }
                                            fetchPriority={
                                                isActive ? "high" : "auto"
                                            }
                                            decoding="async"
                                            width="1200"
                                            height="250"
                                            style={{
                                                width: "100%",
                                                height: "250px",
                                                objectFit: "contain",
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                handleBannerClick(
                                                    banner.business_home_banner_link
                                                )
                                            }
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </Slider>
                </div>
            </div>
        </div>
    );
};

export default NewArrivalsBanner;